import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchWatches, updateWatch } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { errorMessage } from "@/lib/errors";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, ArrowDownUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import WatchThumb from "@/components/WatchThumb";

const SORTS = [
  { value: "sale_date:desc", label: "Sold date — newest first" },
  { value: "sale_date:asc", label: "Sold date — oldest first" },
  { value: "excel_id:desc", label: "Watch ID — high to low" },
  { value: "excel_id:asc", label: "Watch ID — low to high" },
];

/**
 * Inline-editable cell for ebay_order_id / ebay_item_id. Saves on blur or
 * Enter; reverts on Escape. Uses a partial PUT — safe now that the backend
 * uses exclude_unset=True (post 2026-05-07 incident).
 */
function InlineEdit({ id, field, initial, placeholder, onSaved }) {
  const [value, setValue] = useState(initial ?? "");
  const [savedValue, setSavedValue] = useState(initial ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Resync if the parent reloads the row (e.g. after a refresh button).
  useEffect(() => {
    setValue(initial ?? "");
    setSavedValue(initial ?? "");
  }, [initial]);

  const dirty = (value || "") !== (savedValue || "");

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      // Send empty string as null so blank inputs clear the field.
      const payload = { [field]: value.trim() === "" ? null : value.trim() };
      await updateWatch(id, payload);
      setSavedValue(value);
      onSaved?.(id, field, payload[field]);
    } catch (err) {
      toast.error(errorMessage(err, "Could not save"));
      setValue(savedValue);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          inputRef.current?.blur();
        } else if (e.key === "Escape") {
          setValue(savedValue);
          inputRef.current?.blur();
        }
      }}
      placeholder={placeholder}
      disabled={saving}
      className={`h-8 px-2 text-xs font-mono-data bg-[#FBF3D9] border-[#8B6A1F]/35 rounded-sm focus-visible:ring-[#D4AF37]/40 ${
        dirty ? "border-[#D4AF37]/70" : ""
      }`}
      data-testid={`sales-${field}-${id}`}
    />
  );
}

export default function Sales() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("sale_date:desc");

  const [sort, order] = sortKey.split(":");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchWatches({
        status: "SOLD",
        sort,
        order,
        limit: 1000,
      });
      setItems(r.items || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load sales"));
    } finally {
      setLoading(false);
    }
  }, [sort, order]);

  useEffect(() => { load(); }, [load]);

  const onCellSaved = (id, field, newValue) => {
    setItems((prev) =>
      prev.map((w) => (w.id === id ? { ...w, [field]: newValue } : w)),
    );
  };

  const totals = useMemo(() => {
    const count = items.length;
    const revenue = items.reduce((s, w) => s + (Number(w.sale_price) || 0), 0);
    const missing = items.filter((w) => !w.ebay_order_id || !w.ebay_item_id).length;
    return { count, revenue, missing };
  }, [items]);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px]" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-1.5">
            Reconciliation
          </div>
          <h1 className="font-serif-display text-4xl text-[#0A0604]">Sales Ledger</h1>
          <p className="text-xs text-[#3A2A10] mt-2 max-w-xl">
            Quick paste-and-go view of every sold watch. Tab through the eBay Order ID and Item ID
            cells — each cell saves the moment you leave it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <ArrowDownUp size={14} className="text-[#3A2A10]" />
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger
                className="h-9 w-[230px] bg-[#FBF3D9] border-[#8B6A1F]/35 rounded-sm text-xs"
                data-testid="sales-sort"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/35 rounded-sm">
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value} className="text-xs">
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="h-9 bg-transparent border-[#8B6A1F]/45 text-[#0A0604] hover:bg-[#2B2214]/[0.08] rounded-sm text-xs uppercase tracking-wider"
            data-testid="sales-refresh"
          >
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4 mb-6 border border-[#8B6A1F]/35 bg-[#F9F0D4] rounded-sm p-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Sold watches</div>
          <div className="font-mono-data text-2xl text-[#0A0604] mt-1" data-testid="sales-total-count">
            {totals.count}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Total revenue</div>
          <div className="font-mono-data text-2xl text-[#0A0604] mt-1" data-testid="sales-total-revenue">
            {fmtMoney(totals.revenue)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">Missing eBay IDs</div>
          <div
            className={`font-mono-data text-2xl mt-1 ${totals.missing ? "text-amber-600" : "text-[#0A0604]"}`}
            data-testid="sales-missing-ids"
          >
            {totals.missing}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] overflow-x-auto" data-testid="sales-table">
        <table className="w-full text-sm min-w-[960px]">
          <thead>
            <tr className="border-b border-[#8B6A1F]/35">
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">ID</th>
              <th className="px-2 py-3 w-12"></th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">Brand</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">Caliber</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">Sold Date</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap">Sold Amt</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap min-w-[200px]">eBay Order ID</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal whitespace-nowrap min-w-[200px]">eBay Item ID</th>
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-[#3A2A10] text-xs">
                  Loading sold watches…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-16 text-center text-[#3A2A10]" data-testid="sales-empty">
                  <div className="font-serif-display text-2xl text-[#3A2A10] mb-2">No sold watches yet</div>
                  <div className="text-xs">When you mark a watch SOLD, it'll show up here for reconciliation.</div>
                </td>
              </tr>
            ) : (
              items.map((w) => (
                <tr
                  key={w.id}
                  className="border-b border-[#8B6A1F]/30 hover:bg-[#2B2214]/[0.04]"
                  data-testid={`sales-row-${w.id}`}
                >
                  <td className="px-4 py-2 font-mono-data text-[#3A2A10] whitespace-nowrap">
                    {w.excel_id ?? "—"}
                  </td>
                  <td className="px-2 py-2 w-12">
                    <WatchThumb watch={w} size="xs" />
                  </td>
                  <td className="px-4 py-2 text-[#0A0604] whitespace-nowrap">{w.brand || "—"}</td>
                  <td className="px-4 py-2 font-mono-data text-[#0A0604] whitespace-nowrap">{w.caliber || "—"}</td>
                  <td className="px-4 py-2 font-mono-data text-[#3A2A10] whitespace-nowrap">{w.sale_date || "—"}</td>
                  <td className="px-4 py-2 font-mono-data text-[#0A0604] text-right whitespace-nowrap">
                    {fmtMoney(w.sale_price)}
                  </td>
                  <td className="px-2 py-1.5 min-w-[200px]">
                    <InlineEdit
                      id={w.id}
                      field="ebay_order_id"
                      initial={w.ebay_order_id}
                      placeholder="Order ID…"
                      onSaved={onCellSaved}
                    />
                  </td>
                  <td className="px-2 py-1.5 min-w-[200px]">
                    <InlineEdit
                      id={w.id}
                      field="ebay_item_id"
                      initial={w.ebay_item_id}
                      placeholder="Item ID…"
                      onSaved={onCellSaved}
                    />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Link
                      to={`/inventory/${w.id}`}
                      className="inline-flex items-center justify-center h-7 w-7 text-[#3A2A10] hover:text-[#D4AF37] transition-colors"
                      title="Open watch detail"
                      data-testid={`sales-open-${w.id}`}
                    >
                      <ExternalLink size={14} />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-[#3A2A10] mt-3 font-mono-data">
        Tip: Tab moves between cells. Enter saves & exits. Esc reverts.
      </div>
    </div>
  );
}
